@file:Suppress(
    "RETURN_VALUE_NOT_USED",
    "RETURN_VALUE_NOT_USED_COERCION",
)

import androidx.compose.runtime.Composable
import androidx.compose.runtime.ProvidableCompositionLocal
import androidx.compose.runtime.compositionLocalOf
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.navigation.BasePath
import com.varabyte.kobweb.navigation.Router
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import com.varabyte.kobweb.navigation.remove
import com.varabyte.kobweb.silk.defer.DeferringHost
import kotlin.Any
import kotlin.Suppress
import kotlin.Unit
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.renderComposable
import com.wharpe.finsimweb.components.layouts.MarkdownLayout as _com_wharpe_finsimweb_components_layouts_MarkdownLayout
import com.wharpe.finsimweb.components.layouts.PageLayout as _com_wharpe_finsimweb_components_layouts_PageLayout
import com.wharpe.finsimweb.pages.HomePage as _com_wharpe_finsimweb_pages_HomePage

private val LayoutScopeLocal: ProvidableCompositionLocal<Any?> = compositionLocalOf<Any?> { null }

@Composable
private fun <T : Any> currentLayoutScope(): T {
    @Suppress("UNCHECKED_CAST")
    return LayoutScopeLocal.current as? T
        ?: error("Unexpected between Page/Layout scope and the parent Layout which it is providing it. Please report this issue to the Kobweb developers.")
}

@Composable
private fun <T : Any> provideLayoutScope(layoutScope: T, content: @Composable () -> Unit) {
    androidx.compose.runtime.CompositionLocalProvider(LayoutScopeLocal provides layoutScope, content = content)
}

public fun main() {
    AppGlobals.initialize(mapOf("title" to "FinSim"))
    BasePath.set("")
    val router = Router()
    com.varabyte.kobweb.core.init.initKobweb(router) { ctx ->
        ctx.router.registerLayout(
            "com.wharpe.finsimweb.components.layouts.MarkdownLayout",
            parentLayoutId = "com.wharpe.finsimweb.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                com.wharpe.finsimweb.components.layouts.initMarkdownLayout(ctx)
            },
        ) { pageCtx, pageMethod -> 
            _com_wharpe_finsimweb_components_layouts_MarkdownLayout {
                pageMethod(pageCtx)
            }
        }
        ctx.router.registerLayout(
            "com.wharpe.finsimweb.components.layouts.PageLayout",
        ) { pageCtx, pageMethod -> 
            _com_wharpe_finsimweb_components_layouts_PageLayout(pageCtx) {
                provideLayoutScope(this) {
                    pageMethod(pageCtx)
                }
            }
        }
        ctx.router.register(
            "/",
            layoutId = "com.wharpe.finsimweb.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                com.wharpe.finsimweb.pages.initHomePage(ctx)
            },
        ) { pageCtx -> 
            _com_wharpe_finsimweb_pages_HomePage()
        }

    }
    router.addRouteInterceptor {
        path = path.removeSuffix(".html").removeSuffix(".htm")
    }

    com.varabyte.kobweb.silk.init.additionalSilkInitialization = { ctx ->
        com.varabyte.kobweb.silk.init.initSilkWidgets(ctx)
        com.varabyte.kobweb.silk.init.initSilkWidgetsKobweb(ctx)
        ctx.theme.registerStyle("headline-text", com.wharpe.finsimweb.HeadlineTextStyle)
        ctx.theme.registerStyle("subheadline-text", com.wharpe.finsimweb.SubheadlineTextStyle)
        ctx.theme.registerStyle("money", com.wharpe.finsimweb.MoneyStyle)
        ctx.theme.registerStyle("footer", com.wharpe.finsimweb.components.sections.FooterStyle)
        ctx.theme.registerStyle("nav-header", com.wharpe.finsimweb.components.sections.NavHeaderStyle)
        ctx.theme.registerStyle("wordmark", com.wharpe.finsimweb.components.sections.WordmarkStyle)
        ctx.theme.registerStyle("logo-mark", com.wharpe.finsimweb.components.sections.LogoMarkStyle)
        ctx.theme.registerStyle("markdown", com.wharpe.finsimweb.components.layouts.MarkdownStyle)
        ctx.theme.registerStyle("page-content", com.wharpe.finsimweb.components.layouts.PageContentStyle)
        ctx.theme.registerStyle("section", com.wharpe.finsimweb.pages.SectionStyle)
        ctx.theme.registerStyle("eyebrow", com.wharpe.finsimweb.pages.EyebrowStyle)
        ctx.theme.registerStyle("hero-logo", com.wharpe.finsimweb.pages.HeroLogoStyle)
        ctx.theme.registerStyle("badge", com.wharpe.finsimweb.pages.BadgeStyle)
        ctx.theme.registerStyle("section-title", com.wharpe.finsimweb.pages.SectionTitleStyle)
        ctx.theme.registerStyle("card", com.wharpe.finsimweb.pages.CardStyle)
        ctx.theme.registerStyle("card-grid", com.wharpe.finsimweb.pages.CardGridStyle)
        ctx.theme.registerStyle("three-column-grid", com.wharpe.finsimweb.pages.ThreeColumnGridStyle)
        ctx.theme.registerStyle("two-column-grid", com.wharpe.finsimweb.pages.TwoColumnGridStyle)
        ctx.theme.registerStyle("step-number", com.wharpe.finsimweb.pages.StepNumberStyle)
        ctx.theme.registerStyle("disclaimer", com.wharpe.finsimweb.pages.DisclaimerStyle)
        ctx.theme.registerVariant("-circle", com.wharpe.finsimweb.CircleButtonVariant)
        ctx.theme.registerVariant("-uncolored", com.wharpe.finsimweb.UncoloredButtonVariant)
        ctx.theme.registerKeyframes("side-menu-slide-in", com.wharpe.finsimweb.components.sections.SideMenuSlideInAnim)
        com.wharpe.finsimweb.initSiteStyles(ctx)
        com.wharpe.finsimweb.initColorMode(ctx)
        com.wharpe.finsimweb.initStyles(ctx)
        com.wharpe.finsimweb.initTheme(ctx)
    }

    // Dedup any leading slashes after removing the origin, just in case someone typed
    // something like `https://site.com//about` by accident. If we pass `//about` into
    // `tryRoutingTo`, Kobweb will reject it as a protocol-relative URL; instead, we
    // want it to navigate to `/about`
    router.tryRoutingTo(BasePath.remove("/" + window.location.href.removePrefix(window.origin).trimStart('/')), UpdateHistoryMode.REPLACE)

    // For SEO, we may bake the contents of a page in at build time. However, we will
    // overwrite them the first time we render this page with their composable, dynamic
    // versions. Think of this as poor man's hydration :)
    // See also: https://en.wikipedia.org/wiki/Hydration_(web_development)
    val root = document.getElementById("_kobweb-root")!!
    while (root.firstChild != null) { root.removeChild(root.firstChild!!) }

    renderComposable(rootElementId = "_kobweb-root") {
        com.wharpe.finsimweb.AppEntry {
            router.renderActivePage { DeferringHost { it() } }
        }
    }
}
